<!-- PauseSubscriptionConfirmModal.vue
     Modal de confirmação para pausar a renovação automática de uma
     assinatura recorrente ao fim do período corrente.
     ─────────────────────────────────────────────────────────────
     Casos de uso:
     - A assinatura está ativa e cobrando recorrentemente. O usuário
       quer parar a renovação automática, mas continuar usufruindo
       até o fim do ciclo já pago.
     - No backend, o preapproval no Mercado Pago é alterado para
       status "paused" (cancelAtPeriodEnd = true). O usuário mantém
       acesso até EndDate e depois cai no plano Free.
-->

<script setup>
import { onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  // Indica se a ação está em andamento (loading no botão de confirmar).
  isPausing: {
    type: Boolean,
    default: false,
  },
  // Data formatada (dd/MM/yyyy) até quando o usuário mantém acesso.
  // Pode ser null caso a sub não tenha endDate definido.
  endDateFormatted: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["close", "confirm"]);

function close() {
  if (props.isPausing) return;
  emit("close");
}

function confirm() {
  if (props.isPausing) return;
  emit("confirm");
}

// Fecha com Esc
function handleEsc(e) {
  if (e.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEsc);
});
</script>

<template>
  <transition name="modal-fade" appear>
    <div
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pause-sub-title"
      @click.self="close"
    >
      <div class="modal-panel modal-panel--compact">
        <div class="modal-panel__icon">
          <i class="fas fa-pause-circle"></i>
        </div>

        <h4 id="pause-sub-title" class="modal-panel__title">
          Pausar renovação?
        </h4>

        <p class="modal-panel__body">
          A renovação automática da sua assinatura será interrompida.
          <template v-if="endDateFormatted">
            Você manterá acesso ao plano atual
            <strong>até {{ endDateFormatted }}</strong> e depois será
            movido para o plano <strong>Free</strong>.
          </template>
          <template v-else>
            Você manterá acesso ao plano atual até o fim do período pago
            e depois será movido para o plano <strong>Free</strong>.
          </template>
        </p>

        <p class="modal-panel__body modal-panel__body--small">
          Você poderá reativar a assinatura a qualquer momento antes do
          fim do período.
        </p>

        <div class="modal-panel__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isPausing"
            @click="close"
          >
            Voltar
          </button>
          <button
            type="button"
            class="btn btn-pause"
            :disabled="isPausing"
            @click="confirm"
          >
            <span v-if="isPausing" class="btn-spinner"></span>
            <span>{{
              isPausing ? "Pausando..." : "Confirmar pausa"
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c2410c;
  font-size: 1.4rem;
  margin-bottom: 0.25rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
}

.modal-panel__body--small {
  font-size: 0.82rem;
  background: var(--warning-bg);
  border: 1px solid #facc15;
  color: var(--warning-text);
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  line-height: 1.45;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.btn-pause {
  background-color: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.btn-pause:hover:not(:disabled) {
  background-color: #ffedd5;
}

/* Spinner inline para estado de loading */
.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid #fed7aa;
  border-top-color: #9a3412;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transição de entrada/saída do modal */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}
</style>